// The Briefing strip at the TOP of the SmartBox, once a web search has come back
// (frame 40006859:278034).
//
//   🌐  Web search
//
// (It also had "🌐 18 resources" and "⚙ Product, reviews and resources" rows; Saffet asked for
// them to go on 2026-10-27, busy-my-web#688.)
//
import { briefing, countLabel } from './rows'
import { typeTitle } from '@/lib/globalTimelineFeed'
import { isChatTurn, chatLinks } from '@/lib/chatTurn'

// Not to be confused with lib/researchBriefing.js, which is the SELECT-mode briefing
// inside the compose column ("Selected / 3 photos / 2 snippets", frame 40006448:205688).
// That one describes what the user has PICKED; this one describes what the search
// FOUND, and it appears the moment the results land — before anything is picked.

/**
 * "Web search" — the line naming what the strip is briefing.
 *
 * It used to write the QUERY into that line ("Web researching briefing - Buy sunglasses"), which
 * is fine for three words and unusable for the way people actually search: Saffet, 2026-08-26
 * (busy-my-web#218) — "We should only have Web search in the Briefing list as it gets to long if
 * you put the complete thing you searched for… looking for a chocolate cake with three layers and
 * flowers on top with a silver base and candles… it will be so big to save." A briefing line is a
 * fixed-width strip; a sentence in it either wraps the box or truncates to nothing readable.
 *
 * The words are the app's own kind word for a search — the one the global feed's rows, the story
 * rail's kind line and the research listing header all say — taken from `typeTitle` rather than
 * spelled again here, so a web search is called the same thing on every surface. The query itself
 * is not lost: the search view above these rows is showing it.
 */
export function searchBriefingTitle() {
  return typeTitle('research')
}

/**
 * How much the search found. The frame counts "resources" — the sources you can open —
 * which is the links plus the photos, the two kinds that carry a URL. Answer paragraphs
 * are the summary OF those sources, not extra sources, so they are not counted.
 */
export function resourceCount(turn) {
  // A CHAT turn found nothing — it was not a search (busy-my-web#397). Its links are the few the
  // Brain pointed at, and they are the chat briefing's to count (below), not "resources" a results
  // page came back with. This is also what keeps the Work-board from opening itself on a chat
  // answer: the box opens the board on this count.
  if (isChatTurn(turn)) return 0
  const links = (turn?.links || []).length
  const photos = (turn?.photos || []).length
  return links + photos
}

// What a result IS, from the only signals we actually have: its title, snippet and URL.
// Deliberately coarse — this line is a one-glance description of the mix, not a taxonomy,
// and a wrong-but-confident label would be worse than a general one.
const REVIEW = /\b(review|reviewed|reviews|tested|hands[- ]on|comparison|compared|vs\.?|best|top \d|ranking|rated)\b/i
const PRODUCT = /\b(buy|shop|store|price|prices|pricing|deal|deals|sale|order|cart|product|models?)\b|[$£€₺]\s?\d|\b\d+\s?(usd|eur|gbp|try)\b/i
const SHOP_HOST = /(^|\.)(amazon|ebay|etsy|walmart|target|bestbuy|hepsiburada|trendyol|n11|gittigidiyor|aliexpress|argos|currys|johnlewis|zalando|asos)\./i

function hostOf(url) {
  try { return new URL(String(url)).hostname } catch { return '' }
}

function classify(result) {
  const text = `${result?.title || ''} ${result?.snippet || ''}`
  const host = hostOf(result?.url)
  if (SHOP_HOST.test(host) || PRODUCT.test(text)) return 'product'
  if (REVIEW.test(text)) return 'review'
  return 'resource'
}

/**
 * "Product, reviews and resources" — what KIND of material came back.
 *
 * Order is fixed (product → reviews → resources) rather than by frequency, so the same
 * search does not reword its own briefing between renders as results stream in. Kinds
 * with nothing in them are dropped; "resources" is the catch-all and so is nearly always
 * present.
 *
 * @returns {string} empty when there is nothing to describe
 */
export function resourceKinds(turn) {
  const results = [...(turn?.links || []), ...(turn?.photos || [])]
  if (!results.length) return ''

  const found = new Set(results.map(classify))
  const parts = []
  if (found.has('product')) parts.push('Product')
  if (found.has('review')) parts.push('reviews')
  if (found.has('resource')) parts.push('resources')
  if (!parts.length) return ''

  // "a", "a and b", "a, b and c" — the frame's own phrasing.
  if (parts.length === 1) return parts[0]
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

/** Is this turn still working — asked, and nothing back yet? */
export function turnRunning(turn) {
  return !!turn && (!!turn.loading || !!turn.streaming)
}

/**
 * The strip while the search is STILL RUNNING (busy-my-web#359).
 *
 * Saffet: "The Ongoing doesn't show you have started a web search anymore". Inside a story the
 * Briefing is the surface that says what is going on, and it said nothing at all until the results
 * landed — the strip was `null` for the whole of the search, so starting one left the Ongoing
 * looking exactly as it had a second earlier.
 *
 * It is the same card, with its two rows drawn as the #354 shimmer instead of as counts: the slot
 * each number is on its way into, at the width of the words that will replace it, so when "18
 * resources" arrives it replaces the bar in place and the card does not reflow. That is why this is
 * a pending row and not the old "0 resources" — a count that flickers from 0 reads as a fault,
 * where a shimmer reads as the answer being on its way.
 *
 * The TITLE says which of the two asks is running: the globe's web search, or a plain question in
 * the story's chat (the turn carries `web`). Nothing else about the card differs.
 */
function runningBriefing(turn, packs) {
  return briefing({
    kind: 'search',
    title: turn.web === false ? 'Thinking…' : `${searchBriefingTitle()}…`,
    titleIcon: 'global',
    // No count rows, not even as shimmers (busy-my-web#688, see searchBriefing).
    rows: [],
    ...savedInto(packs),
  })
}

/**
 * WHERE THIS SESSION'S RESULTS WENT (busy-my-web#364).
 *
 * Saffet: "when you save to a research pack it should be in the search Briefing". Filing results
 * briefs the pack itself for as long as it leads (#214); the moment the next question is asked the
 * strip is a search briefing again and the pack has left the box altogether.
 *
 * It rides on the briefing's `selected` list — the one the strip already draws as "name + View ↗",
 * with Close on the one that IS open — under its own heading. That list is a shape, not a meaning:
 * `selectedLabel` exists precisely because the comparison briefing calls the same rows "Sources"
 * (lib/briefings/rows.js). Here they are the destinations, so the heading says so.
 *
 * Nothing saved yet, no heading and no rows: a search that has kept nothing has no destination to
 * name, and an empty "Saved to" would read as a save that failed.
 */
const savedInto = (packs) => ((packs || []).length
  ? { selected: packs, selectedLabel: 'Saved to' }
  : {})

/**
 * The strip for a CHAT turn (busy-my-web#397) — "Chat / 2 links", never "Web search / N resources".
 *
 * Saffet: the Ongoing's chat "should be about the chat like brain storming with some links not look
 * same as web search". While the Brain is thinking the card is the same shimmer a search gets, titled
 * "Thinking…" (#359); once it has answered, the card names the chat and counts only the links the
 * answer pointed at — the same few the workspace lists under the prose (lib/chatTurn's `chatLinks`,
 * uncapped here so the number is honest). An answer with no links briefs nothing: the answer is on
 * screen, and a card that only said "Chat" would be a heading over an empty box.
 */
function chatBriefing(turn, packs) {
  if (turnRunning(turn)) return runningBriefing(turn, packs)
  const count = chatLinks(turn, Number.POSITIVE_INFINITY).length
  if (!count) return null
  return briefing({
    kind: 'search',
    title: 'Chat',
    titleIcon: 'message-text',
    rows: [{ key: 'links', icon: 'global', text: countLabel(count, 'link') }],
    ...savedInto(packs),
  })
}

/**
 * The whole strip, or null when there is nothing to brief on yet.
 *
 * A running turn gets the shimmering card above; a finished one with nothing to show is still
 * null — an ask that came back without a single resource has no counts to state, and the answer
 * itself is on screen in the workspace.
 *
 * @param turn the latest turn
 * @param packs the Research Packs this session has already filed into
 *        (lib/researchSavedPacks.js) — listed under the counts as "Saved to", each openable.
 */
export function searchBriefing(turn, packs = []) {
  if (!turn) return null
  if (isChatTurn(turn)) return chatBriefing(turn, packs)
  const count = resourceCount(turn)
  if (!count) return turnRunning(turn) ? runningBriefing(turn, packs) : null

  return briefing({
    kind: 'search',
    title: searchBriefingTitle(),
    titleIcon: 'global',
    // Saffet 2026-10-27 (busy-my-web#688): "Can we remove Reviews and Resources in the Brefing".
    // The "18 resources" and "Product, reviews and resources" rows are gone; the strip names the
    // search and lists where its results were saved. `resourceCount` still decides whether there
    // is a strip at all (and opens the Work-board), and `resourceKinds` stays for any caller.
    rows: [],
    ...savedInto(packs),
  })
}

/**
 * DOES THE SEARCH STILL LEAD THE STRIP? (busy-my-web#532)
 *
 * Saffet, after making a pack out of a search inside an Ongoing: "if I click the briefing I should
 * see the briefing of the ongoing."
 *
 * A search inside a story is the more immediate thing WHILE IT IS ON SCREEN — you just ran it, and
 * the workspace is the middle. That is the rule the resolution order was written for and it is
 * unchanged. What it could not tell was the difference between "the results are in front of me" and
 * "a session is lying around in the store": closing the search view, opening the pack it was filed
 * into, or stepping back to the Briefing's root all leave the turns exactly where they were said
 * (lib/researchNav.js — closing a chat is never a deletion), and the strip went on briefing them
 * over the story the user was standing in, with the way back out that a story briefing carries.
 *
 * So the search keeps the strip unless BOTH are true: the user is standing in a PLACE the strip can
 * describe instead (a briefed story — the only subject that outlives the session), and the session
 * is no longer live.
 *
 * …and OFF the story, a search that BELONGS to an Ongoing leads only while it is live (Pouria,
 * 2026-10-01). This comment used to say "on home … nothing changes at all", and that contradicted
 * #532's own recorded requirement (lib/researchNav.js, Saffet: "when I click home I should not see
 * this ongoing anymore in the briefing and the middle"): walking home closes the story's search
 * (`homeArrivalEffects`), home has no story briefed to hand the strip to, and so the closed session
 * went on briefing "Home › Web search · 22 resources · Saved to <the story's pack>" on home. A search
 * started ON home (no `ongoingSession`) is unchanged — it leads whether or not it is open, exactly as
 * before; the Ongoing's search is still one click away behind that story's chat chip.
 *
 * A pack that was just saved into leads over both, as it has since busy-my-web#214 — `packLeads` is
 * checked here so the one question "who leads" has one answer.
 *
 * @param {{ live?: boolean, packLeads?: boolean, placeBriefed?: boolean, ongoingSession?: boolean }} state
 *   live           the session is what the screen is about (lib/researchNav's `researchLive`)
 *   packLeads      a pack was just saved into (briefing store's `packLead`)
 *   placeBriefed   a story is briefed underneath — somewhere to hand the strip back to
 *   ongoingSession the session was run for an Ongoing (`research/targetOngoingId`)
 * @returns {boolean}
 */
export function searchBriefingLeads({ live = false, packLeads = false, placeBriefed = false, ongoingSession = false } = {}) {
  if (packLeads) return false
  if (!placeBriefed && !ongoingSession) return true
  return !!live
}

/**
 * WHICH TURN THE STRIP DESCRIBES — or none, for a search that has not run yet (busy-my-web#578).
 *
 * Saffet, opening a new web search inside an Ongoing after the last one was saved to a pack: "Why is
 * there 23 resources and review and resources when I started a new web search". The strip read the
 * session's LATEST TURN whatever state the session was in — and inside a story that turn outlives
 * the view: opening the pack closes the search (`research/closeChat`, #533) and entering the story
 * restores its kept chat closed (`research/loadSession`, #360), both keeping every turn. Arming the
 * globe again made the session count as live (#532's `researchLive` includes `webSearchOn`), so the
 * new, unsent search was briefed with the old one's counts and its "Saved to" receipt (#364).
 *
 * Inside a story those turns are only on screen while the chat is OPEN — that is the whole of
 * `researchOwnsMiddle`'s rule for an ongoing (lib/searchMode.js) — and the rail agrees: it answers
 * "Search opened / Ready when you are" over a closed session (lib/researchTimeline.js). So with a
 * story briefed and the session closed there is no turn to describe: the fresh search briefs
 * exactly as a story that was never searched does, and the moment it is sent (`research/start`
 * opens the session and appends its own turn) the #359 shimmer and then its own counts take over.
 * Nothing is dropped: the old turns stay in the store, behind the story's "Chat (N)" door and the
 * pack they were filed into. Off a story nothing changes — the #532 rule for home and module pages.
 *
 * @param turns the session's turns (`research/turns`)
 * @param {{ active?: boolean, placeBriefed?: boolean }} state
 *   active       the session is open (`research/active`)
 *   placeBriefed a story is briefed underneath (see `searchBriefingLeads`)
 * @returns {object|null} the turn to hand `searchBriefing`
 */
export function briefedSearchTurn(turns, { active = false, placeBriefed = false } = {}) {
  const list = turns || []
  if (!list.length) return null
  if (placeBriefed && !active) return null
  return list[list.length - 1]
}

/**
 * WHAT THE STRIP BRIEFS RIGHT AFTER A SAVE (Saffet, 2026-10-21).
 *
 *   "When I am in an ongoing and I save a search to a new research pack the Briefing still shows me
 *    the web 14 resources found when it should show me how many research packs, Possible
 *    connections and conversations at that moment as soon as I click save."
 *   "When I save something to a research pack the briefing should show everything as soon as I
 *    save. Not just 1 research pack."
 *
 * A save used to hand the strip to the ONE pack it filed into (`briefSavedPack`, Pouria 2026-08-17)
 * — and only when that pack could already be found on the story; a brand-new pack that the story
 * had not caught up with left the search's own "N resources" card in place. The save ends on the
 * Ongoing (#476's frames 40006448:210248 / 40007687:334837 draw "Briefing · <ongoing> / N research
 * packs …"), so the Ongoing's own Briefing is what leads after it: every pack, the possible
 * connections and the conversations, in the one card. With no story known yet (a save that is still
 * creating its Ongoing) the saved pack keeps the lead as before.
 *
 * @param {{ saved?: boolean, pack?: object|null, story?: object|null }} state
 *   saved  a save has just been made (briefing store's `packLead`)
 *   pack   the pack it filed into, when known
 *   story  the Ongoing it belongs to, when known
 * @returns {'story'|'pack'|null}  who leads because of the save; null when no save is leading
 */
export function afterSaveSubject({ saved = false, pack = null, story = null } = {}) {
  if (!saved) return null
  if (story?.id) return 'story'
  return pack ? 'pack' : null
}
